import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import DataMatrix from 'react-datamatrix-svg';
import { useGetTourQuery, useGetPartOrderByIdQuery } from 'services/kewloxApi';
import { PackageOrder } from 'app/models/PackageOrder';
import { parsePackageEntryName } from 'app/containers/OrderDetails/PackagesTab/index';
import { selectAllColors } from 'store/slices/colorSlice';
import { PartOrder } from 'app/models/PartOrder';
import { selectPartOrderById } from 'store/slices/partOrderSlice';
import { AppRootState } from 'index';
import { Delivery } from 'app/models/Delivery';

interface OrderPackagesProps {
  partOrderId: number;
  compact: boolean;
}

const OrderPackages: React.FC<OrderPackagesProps> = ({
  partOrderId,
  compact,
}) => {
  const allColors = useSelector(selectAllColors);
  const partOrder = useSelector((state: AppRootState) =>
    selectPartOrderById(state, partOrderId),
  ) as PartOrder | undefined;

  // Trigger fetch if not in store
  const { isLoading } = useGetPartOrderByIdQuery(partOrderId, {
    skip: !!partOrder?.packages,
  });

  if (isLoading || !partOrder?.packages) {
    return (
      <div className="text-gray-500 py-2">Chargement commande {partOrderId}...</div>
    );
  }

  const packages = partOrder.packages || [];

  if (packages.length === 0) {
    return null;
  }

  if (compact) {
    // En mode compact, retourner juste les packages sans wrapper
    return (
      <>
        {packages.map((pkg: PackageOrder) => (
          <div
            key={pkg.id}
            className="flex flex-col items-center p-1"
          >
            <DataMatrix msg={`${pkg.id}`} dim={60} pal={['#000', '#fff']} />
            <div className="text-xs text-gray-600 mt-0.5">{pkg.id}</div>
          </div>
        ))}
      </>
    );
  }

  return (
    <div className="mb-8">
      <h2 className="text-xl font-semibold mb-4 border-b pb-2">
        Commande #{partOrder.order.id} - {partOrder.orderInfo.firstname} {partOrder.orderInfo.lastname}
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8">
        {packages.map((pkg: PackageOrder) => (
          <div
            key={pkg.id}
            className="flex flex-col items-center border rounded-lg shadow bg-white p-6"
          >
            <DataMatrix msg={`${pkg.id}`} dim={180} pal={['#000', '#fff']} />
            <div className="mt-4 text-lg font-semibold text-center">
              {pkg.uniqueDescr
                ? pkg.uniqueDescr
                : pkg.name
                ? parsePackageEntryName(pkg.name, allColors as any)
                : `Colis ${pkg.id}`}
            </div>
            <div className="text-gray-500 mt-2">{pkg.id}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

const TourPackagesQRCodesPage: React.FC = () => {
  const { tourId } = useParams<{ tourId: string }>();
  const [compact, setCompact] = useState(false);

  const { data: tour, isLoading: tourLoading } = useGetTourQuery(
    Number(tourId),
    { skip: !tourId },
  );

  if (tourLoading || !tour) {
    return (
      <div className="container mx-auto py-8 text-center">
        Chargement de la tournée...
      </div>
    );
  }

  // Get partOrder IDs from deliveries
  const partOrderIds: number[] = (tour.orders || [])
    .filter((d: Delivery) => d.partOrder)
    .map((d: Delivery) => d.partOrder as number);

  const uniquePartOrderIds: number[] = [...new Set(partOrderIds)];

  return (
    <div className="container mx-auto py-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">
          QR codes - Tournée #{tourId} ({uniquePartOrderIds.length} commandes)
        </h1>
        <button
          onClick={() => setCompact(!compact)}
          className={`px-4 py-2 rounded border ${
            compact
              ? 'bg-blue-600 text-white border-blue-600'
              : 'bg-white text-gray-700 border-gray-300'
          }`}
        >
          {compact ? 'Mode compact ✓' : 'Mode compact'}
        </button>
      </div>

      {uniquePartOrderIds.length === 0 ? (
        <div className="text-gray-500">Aucune commande dans cette tournée</div>
      ) : compact ? (
        // Mode compact: grille plate sans regroupement par commande
        <div className="grid grid-cols-6 md:grid-cols-10 lg:grid-cols-14 xl:grid-cols-18 gap-1">
          {uniquePartOrderIds.map(partOrderId => (
            <OrderPackages
              key={partOrderId}
              partOrderId={partOrderId}
              compact={compact}
            />
          ))}
        </div>
      ) : (
        // Mode normal: groupé par commande
        uniquePartOrderIds.map(partOrderId => (
          <OrderPackages
            key={partOrderId}
            partOrderId={partOrderId}
            compact={compact}
          />
        ))
      )}
    </div>
  );
};

export default TourPackagesQRCodesPage;
